import { useCallback, useEffect, useRef, useState } from 'react';
import OdenPlayer, {
  type OdenPlayerProps,
  type QualityOption,
  type AudioOption,
  type CaptionTrack,
} from '@nni/oden-player';
import { refreshPlaybackUrl } from '../api/playback';
import { useProgressSync } from '../hooks/useProgressSync';

interface HlsOdenPlayerProps extends Omit<OdenPlayerProps, 'qualities' | 'audioTracks' | 'captions' | 'onSelectQuality' | 'onSelectAudio'> {
  src: string;
  /** Required so progress sync targets the right Firestore doc. */
  mediaId: string;
  /** Resume position to seek to once metadata loads. */
  initialPositionSec?: number;
}

type HlsModule = typeof import('hls.js');

export function HlsOdenPlayer({ src, mediaId, initialPositionSec, ...rest }: HlsOdenPlayerProps) {
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const hlsRef = useRef<InstanceType<HlsModule['default']> | null>(null);

  const [qualities, setQualities] = useState<QualityOption[]>([]);
  const [audioTracks, setAudioTracks] = useState<AudioOption[]>([]);
  const [captions, setCaptions] = useState<Array<CaptionTrack & { src: string; default?: boolean }>>([]);

  const handleVideoReady = useCallback((el: HTMLVideoElement | null) => {
    setVideoEl(el);
  }, []);

  // Resume position once metadata is loaded.
  useEffect(() => {
    if (!videoEl || !initialPositionSec) return;
    const seek = () => {
      if (videoEl.duration && videoEl.currentTime < initialPositionSec) {
        videoEl.currentTime = initialPositionSec;
      }
    };
    videoEl.addEventListener('loadedmetadata', seek, { once: true });
    return () => videoEl.removeEventListener('loadedmetadata', seek);
  }, [videoEl, initialPositionSec]);

  // Wire HLS source.
  useEffect(() => {
    if (!videoEl) return;
    const isM3u8 = src.endsWith('.m3u8') || src.includes('.m3u8?');

    if (!isM3u8) {
      videoEl.src = src;
      return;
    }

    // Safari (and iOS) plays HLS natively — much smoother than hls.js for
    // the cases where it works.
    if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      videoEl.src = src;
      return;
    }

    // Everything else: dynamic import keeps the hls.js bundle out of the
    // initial chunk so the library list loads fast.
    let cancelled = false;
    let hls: InstanceType<HlsModule['default']> | null = null;
    import('hls.js').then((mod) => {
      if (cancelled) return;
      const Hls = mod.default;
      if (!Hls.isSupported()) return;

      hls = new Hls({
        enableWorker: true,
        // Keep the last few playlists fresh — the API may rotate the token URL
        // mid-stream when its 6 h TTL nears expiry.
        manifestLoadingMaxRetry: 4,
        manifestLoadingRetryDelay: 500,
      });
      hlsRef.current = hls;

      hls.loadSource(src);
      hls.attachMedia(videoEl);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        const levels: QualityOption[] = [
          { id: 'auto', label: 'Auto' },
          ...hls!.levels.map((lvl, i) => ({
            id: String(i),
            label: lvl.height ? `${lvl.height}p` : `${Math.round((lvl.bitrate || 0) / 1000)}k`,
          })),
        ];
        setQualities(levels);

        setAudioTracks(
          hls!.audioTracks.map((a) => ({
            id: String(a.id),
            label: a.name || a.lang || `Audio ${a.id}`,
          })),
        );

        setCaptions(
          hls!.subtitleTracks.map((s) => ({
            id: String(s.id),
            label: s.name || s.lang || `Subtitles ${s.id}`,
            lang: s.lang || undefined,
            // hls.js owns cue delivery via the <video> textTracks; the player
            // only needs to know the entry exists.
            src: '',
          })),
        );
      });

      hls.on(Hls.Events.ERROR, async (_e, data) => {
        if (!data.fatal) return;
        // Token-expiry comes back as a network error on the manifest fetch.
        // Swap in a fresh signed URL and resume from current position.
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR && data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR) {
          try {
            const fresh = await refreshPlaybackUrl(mediaId);
            const t = videoEl.currentTime;
            hls!.loadSource(fresh.playbackUrl);
            const onLoaded = () => {
              if (videoEl.duration && t > 0) videoEl.currentTime = t;
              videoEl.removeEventListener('loadedmetadata', onLoaded);
            };
            videoEl.addEventListener('loadedmetadata', onLoaded);
            return;
          } catch {
            // fall through to the recovery path below
          }
        }
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls!.startLoad();
        else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls!.recoverMediaError();
      });
    });

    return () => {
      cancelled = true;
      hls?.destroy();
      hlsRef.current = null;
    };
  }, [videoEl, src, mediaId]);

  useProgressSync({ mediaId, videoEl });

  const onSelectQuality = useCallback((id: string) => {
    if (!hlsRef.current) return;
    hlsRef.current.currentLevel = id === 'auto' ? -1 : parseInt(id, 10);
  }, []);

  const onSelectAudio = useCallback((id: string) => {
    if (!hlsRef.current) return;
    hlsRef.current.audioTrack = parseInt(id, 10);
  }, []);

  return (
    <OdenPlayer
      {...rest}
      src={src}
      contentType="application/vnd.apple.mpegurl"
      qualities={qualities}
      audioTracks={audioTracks}
      captions={captions}
      onSelectQuality={onSelectQuality}
      onSelectAudio={onSelectAudio}
      onVideoReady={handleVideoReady}
    />
  );
}
